import { useState, useEffect } from 'react'
import type CrmFundraisingDonation from '@/types/serializers/CrmFundraisingDonation'
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import DonationShow from '../Show'

interface ShowProps {
  donation: CrmFundraisingDonation
}

interface DonationDetailSheetProps {
  donationId: number | null
  onOpenChange: (open: boolean) => void
}

export function DonationDetailSheet({ donationId, onOpenChange }: DonationDetailSheetProps) {
  const [data, setData] = useState<ShowProps | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // Bumped after an in-sheet mutation to refetch the (now updated) donation.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!donationId) {
      setData(null)
      setError(false)
      return
    }
    // `active` guards every state write so a superseded request (selection
    // changed mid-flight) can't flip loading/data/error for the current one.
    let active = true
    setLoading(true)
    setError(false)

    const controller = new AbortController()
    fetch(`/fundraising/donations/${donationId}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load')
        return res.json()
      })
      .then((json) => { if (active) setData(json) })
      .catch((err) => {
        if (active && err.name !== 'AbortError') setError(true)
      })
      .finally(() => { if (active) setLoading(false) })

    return () => {
      active = false
      controller.abort()
    }
  }, [donationId, reloadKey])

  return (
    <Sheet open={!!donationId} onOpenChange={onOpenChange}>
      <SheetContent className="w-[700px] sm:max-w-[700px] overflow-y-auto p-0" aria-describedby={undefined}>
        <VisuallyHidden.Root>
          <SheetTitle>Détail du don</SheetTitle>
        </VisuallyHidden.Root>
        {loading ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : error ? (
          <div className="space-y-3 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Impossible de charger le don.
            </p>
            <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
              Réessayer
            </Button>
          </div>
        ) : data ? (
          <div className="p-6">
            <DonationShow
              donation={data.donation}
              inSheet
              onMutated={() => setReloadKey((k) => k + 1)}
              onClose={() => onOpenChange(false)}
            />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
